import { logger } from '../utils/logger';

const ML_API_BASE_URL = '/api/ml';

export interface MLAnomaly {
  index: number;
  timestamp: number;
  datetime: string;
  value: number;
  severity: 'high' | 'medium';
}

export interface MLPrediction {
  timestamps: number[];
  values: number[];
  /** flat_low = prolonged near-zero connectivity (not "healthy stability") */
  trend: 'increasing' | 'decreasing' | 'stable' | 'flat_low';
  confidence: number;
}

export interface MLPattern {
  type: string;
  start_index?: number;
  end_index?: number;
  index?: number;
  timestamp?: number;
  start_timestamp?: number;
  end_timestamp?: number;
  duration_minutes?: number;
  drop_percentage?: number;
  average_value?: number;
  severity: 'critical' | 'major' | 'minor';
}

export interface HealthScore {
  score: number;
  current: number;
  average: number;
  stability: number;
  trend: 'improving' | 'degrading' | 'stable' | 'severe';
  /** severe_outage: uniformly critical series — anomaly counts may be suppressed */
  regime?: 'normal' | 'stressed' | 'severe_outage';
  /** Same series as main dashboard chart (merit-nt). */
  ml_datasource?: 'merit-nt' | 'network_score' | string;
}

export interface MLAnomaliesBundle {
  anomalies: MLAnomaly[];
  total_points: number;
  anomaly_count: number;
  ml_datasource?: string;
}

export interface MLInsights {
  health_score: HealthScore;
  anomalies: number;
  patterns_detected: number;
  prediction: MLPrediction | null;
  summary: string;
  ml_datasource?: string;
}

export class MLService {
  /**
   * Detect anomalies in connectivity data
   */
  static async detectAnomalies(hours: number = 24): Promise<MLAnomaly[]> {
    const bundle = await MLService.getAnomaliesBundle(hours);
    return bundle?.anomalies ?? [];
  }

  /** Full /api/ml/anomalies payload (counts + datasource). */
  static async getAnomaliesBundle(hours: number = 168): Promise<MLAnomaliesBundle | null> {
    try {
      const url = `${ML_API_BASE_URL}/anomalies?hours=${hours}`;
      const response = await fetch(url);
      if (!response.ok) return null;
      const data = await response.json();
      if (data.error) return null;
      return {
        anomalies: data.anomalies || [],
        total_points: Number(data.total_points) || 0,
        anomaly_count: Number(data.anomaly_count) || 0,
        ml_datasource: data.ml_datasource,
      };
    } catch (error) {
      logger.error('Error detecting anomalies:', error);
      return null;
    }
  }

  /**
   * Get connectivity predictions
   */
  static async getPredictions(
    hours: number = 168,
    forecastHours: number = 6
  ): Promise<MLPrediction | null> {
    try {
      const url = `${ML_API_BASE_URL}/predict?hours=${hours}&forecast_hours=${forecastHours}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.error) {
        logger.warn('ML prediction error:', data.error);
        return null;
      }
      return data;
    } catch (error) {
      logger.error('Error getting predictions:', error);
      return null;
    }
  }

  /**
   * Detect patterns in connectivity data
   */
  static async detectPatterns(hours: number = 168): Promise<MLPattern[]> {
    try {
      const url = `${ML_API_BASE_URL}/patterns?hours=${hours}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.patterns || [];
    } catch (error) {
      logger.error('Error detecting patterns:', error);
      return [];
    }
  }

  /**
   * Get network health score
   */
  static async getHealthScore(hours: number = 24): Promise<HealthScore | null> {
    try {
      const url = `${ML_API_BASE_URL}/health?hours=${hours}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.error) {
        return null;
      }
      return data;
    } catch (error) {
      logger.error('Error getting health score:', error);
      return null;
    }
  }

  /**
   * Get comprehensive ML insights
   */
  static async getInsights(hours: number = 168): Promise<MLInsights | null> {
    try {
      const url = `${ML_API_BASE_URL}/insights?hours=${hours}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.error) {
        return null;
      }
      return data;
    } catch (error) {
      logger.error('Error getting ML insights:', error);
      return null;
    }
  }
}
